/**
 * Ein Mangel im Detail: Kopfdaten, Fotos, Maßnahmen samt Material, Verlauf — und die Aktionen, die
 * der Betrachter darf (`availableActions`). Der Verlauf kommt nur aus dem Einzelabruf; die Liste
 * lässt ihn weg, deshalb wird beim Öffnen nachgeladen.
 *
 * Eine Aktion schickt der Dialog ab und zeigt danach den Stand, den der Server zurückgibt. Ein 409
 * heißt, jemand anderes war schneller — dann wird neu geladen, statt den alten Stand zu zeigen.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Plus, Trash2 } from "lucide-react";

import { userAPI } from "../../UserAPI";
import WarrantySection from "./WarrantySection";
import { formatLegalDate } from "./claimModel";
import { useDefectsAPI, type Defect, type DefectAction, type DefectStatus } from "../../DefectsAPI";
import {
  MANUAL_MEASURE_KINDS,
  SEVERITIES,
  SEVERITY_LABEL,
  actionPayload,
  availableActions,
  canSubmit,
  defectTitle,
  formatDateTime,
  photoUrl,
  toDateTimeLocal,
  userLabel,
  type ActionDraft,
  type Viewer,
} from "./defectModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const STATUS_VARIANT: Record<DefectStatus, "default" | "secondary" | "success" | "outline" | "warning"> = {
  reported: "warning",
  open: "default",
  resolved: "success",
  closed: "secondary",
  discarded: "outline",
};

export function DefectStatusBadge({ status }: { status: DefectStatus }) {
  const { t } = useTranslation();

  return <Badge variant={STATUS_VARIANT[status] ?? "outline"}>{t(`defectsPanel.status.${status}`)}</Badge>;
}

const emptyMaterial = () => ({ name: "", quantity: "", unit: "", billable: true });

interface Props {
  /** Die Zeile aus der Liste; `undefined` schließt den Dialog. */
  defect?: Defect;
  customer: string;
  /** `null`, solange die Nutzerliste lädt. */
  users: Record<string, any> | null;
  viewer: Viewer;
  onClose: () => void;
  /** Nach jeder Änderung, damit die Liste nachzieht. */
  onChanged: () => void;
}

export default function DefectDetailDialog({ defect: row, customer, users, viewer, onClose, onChanged }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defectsAPI = useDefectsAPI(customer);

  const [defect, setDefect] = useState<Defect | undefined>(row);
  const [action, setAction] = useState<DefectAction | undefined>(undefined);
  const [draft, setDraft] = useState<ActionDraft>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const nameLabels = {
    system: t("defectsPanel.system"),
    documentAnalysis: t("defectsPanel.documentAnalysis"),
    unknown: t("defectsPanel.unknownUser"),
    loading: "…",
  };
  /** Wer gehandelt hat — ohne Person war es das System. */
  const actorName = (id: string | null | undefined) => userLabel(users, id, { ...nameLabels, empty: nameLabels.system });
  /** Wer verantwortlich ist — ohne Person niemand. */
  const assigneeName = (id: string | null | undefined) => userLabel(users, id, { ...nameLabels, empty: "—" });
  const rowId = row?._id;

  useEffect(() => {
    setDefect(row);
    setAction(undefined);
    setError(undefined);
    if (!rowId) return;

    let cancelled = false;
    defectsAPI
      .getDefect(rowId)
      // Der Einzelabruf liefert Verlauf, `assetInfo` und `overdue` selbst.
      .then((full) => !cancelled && setDefect(full))
      .catch((loadError: any) => !cancelled && setError(loadError?.message ?? String(loadError)));

    return () => {
      cancelled = true;
    };
  }, [rowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const actions = useMemo(() => (defect ? availableActions(defect, viewer) : []), [defect, viewer]);
  const userOptions = useMemo(
    () =>
      Object.keys(users ?? {})
        .map((id) => ({ id, label: userLabel(users, id, { system: t("defectsPanel.system"), unknown: t("defectsPanel.unknownUser"), loading: "…", empty: "—" }) }))
        .sort((a, b) => a.label.localeCompare(b.label, "de")),
    [users, t],
  );

  const startAction = (next: DefectAction) => {
    if (!defect) return;
    setError(undefined);
    setAction(next);
    setDraft({
      severity: defect.severity,
      // Beim Sichten heißt leer „beibehalten" — sonst ginge die Zuweisung samt Push noch einmal hinaus.
      assignee: next === "triage" ? undefined : defect.assignee ?? undefined,
      deadline: toDateTimeLocal(defect.deadline),
      measureKind: "repair",
      materials: [],
    });
  };

  const submit = async () => {
    if (!defect || !action) return;
    setSubmitting(true);
    setError(undefined);

    try {
      const updated = await defectsAPI.act(defect._id, action, actionPayload(action, draft));
      // Die Antwort ist die Ansicht des Servers (`getDefectView`), samt `overdue` und `assetInfo`.
      setDefect(updated);
      setAction(undefined);
      toast.success(t("defectsPanel.actionDone", { action: t(`defectsPanel.actions.${action}`) }));
      onChanged();
    } catch (actionError: any) {
      setError(actionError?.message ?? String(actionError));
      // Ein überholter Stand: neu lesen, damit die Knöpfe zum echten Status passen.
      defectsAPI
        .getDefect(defect._id)
        .then(setDefect)
        .catch(() => undefined);
    } finally {
      setSubmitting(false);
    }
  };

  const setMaterial = (index: number, change: Partial<ReturnType<typeof emptyMaterial>>) =>
    setDraft((current) => ({
      ...current,
      materials: (current.materials ?? []).map((m, i) => (i === index ? { ...m, ...change } : m)),
    }));

  const assigneeSelect = (optional: boolean) => (
    <div className="flex flex-col gap-1">
      <Label>{t("defectsPanel.form.assignee")}</Label>
      <Select
        value={draft.assignee ?? ""}
        onValueChange={(assignee) => setDraft((current) => ({ ...current, assignee }))}
      >
        <SelectTrigger aria-label={t("defectsPanel.form.assignee")}>
          <SelectValue placeholder={optional ? t("defectsPanel.form.keepAssignee") : t("defectsPanel.form.chooseAssignee")} />
        </SelectTrigger>
        <SelectContent>
          {userOptions.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {user.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const reasonField = (label: string) => (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Textarea value={draft.reason ?? ""} onChange={(e) => setDraft((current) => ({ ...current, reason: e.target.value }))} />
    </div>
  );

  const actionForm = () => {
    switch (action) {
      case "triage":
        return (
          <>
            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.form.severity")}</Label>
              <Select
                value={draft.severity}
                onValueChange={(severity) => setDraft((current) => ({ ...current, severity: severity as any }))}
              >
                <SelectTrigger aria-label={t("defectsPanel.form.severity")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((severity) => (
                    <SelectItem key={severity} value={severity}>
                      {SEVERITY_LABEL[severity]} — {t(`defectsPanel.severity.${severity}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {defect?.reportedSeverity ? (
                <span className="text-xs text-muted-foreground">
                  {t("defectsPanel.form.reportedAs", { value: t(`defectsPanel.reportedSeverity.${defect.reportedSeverity}`) })}
                </span>
              ) : null}
            </div>
            {assigneeSelect(true)}
          </>
        );
      case "assign":
        return assigneeSelect(false);
      case "setDeadline":
        return (
          <>
            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.form.deadline")}</Label>
              <Input
                type="datetime-local"
                value={draft.deadline ?? ""}
                onChange={(e) => setDraft((current) => ({ ...current, deadline: e.target.value }))}
              />
              {!viewer.dispatcher ? (
                <span className="text-xs text-muted-foreground">{t("defectsPanel.form.onlyShorten")}</span>
              ) : null}
            </div>
            {reasonField(t("defectsPanel.form.reason"))}
          </>
        );
      case "resolve":
        return (
          <>
            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.form.measureKind")}</Label>
              <Select
                value={draft.measureKind}
                onValueChange={(measureKind) => setDraft((current) => ({ ...current, measureKind: measureKind as any }))}
              >
                <SelectTrigger aria-label={t("defectsPanel.form.measureKind")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_MEASURE_KINDS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {t(`defectsPanel.measureKind.${kind}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.form.measureDescription")}</Label>
              <Textarea
                value={draft.measureDescription ?? ""}
                onChange={(e) => setDraft((current) => ({ ...current, measureDescription: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("defectsPanel.form.materials")}</Label>
              {(draft.materials ?? []).map((material, index) => (
                <div key={index} className="grid grid-cols-[1fr_6rem_5rem_auto_auto] items-center gap-2">
                  <Input
                    aria-label={t("defectsPanel.material.name")}
                    placeholder={t("defectsPanel.material.name")}
                    value={material.name}
                    onChange={(e) => setMaterial(index, { name: e.target.value })}
                  />
                  <Input
                    aria-label={t("defectsPanel.material.quantity")}
                    placeholder={t("defectsPanel.material.quantity")}
                    inputMode="decimal"
                    value={material.quantity}
                    onChange={(e) => setMaterial(index, { quantity: e.target.value })}
                  />
                  <Input
                    aria-label={t("defectsPanel.material.unit")}
                    placeholder={t("defectsPanel.material.unit")}
                    value={material.unit}
                    onChange={(e) => setMaterial(index, { unit: e.target.value })}
                  />
                  <label className="flex items-center gap-1 text-xs">
                    <Checkbox
                      checked={material.billable}
                      onCheckedChange={(billable) => setMaterial(index, { billable: billable === true })}
                    />
                    {t("defectsPanel.material.billable")}
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("defectsPanel.material.remove")}
                    onClick={() =>
                      setDraft((current) => ({ ...current, materials: (current.materials ?? []).filter((_, i) => i !== index) }))
                    }
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => setDraft((current) => ({ ...current, materials: [...(current.materials ?? []), emptyMaterial()] }))}
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                {t("defectsPanel.material.add")}
              </Button>
            </div>
          </>
        );
      case "reopen":
      case "discard":
        return reasonField(t("defectsPanel.form.reason"));
      case "close":
        return <p className="text-sm text-muted-foreground">{t("defectsPanel.form.closeHint")}</p>;
      case "comment":
        return (
          <div className="flex flex-col gap-1">
            <Label>{t("defectsPanel.form.comment")}</Label>
            <Textarea value={draft.comment ?? ""} onChange={(e) => setDraft((current) => ({ ...current, comment: e.target.value }))} />
          </div>
        );
      default:
        return null;
    }
  };

  const describeEvent = (entry: NonNullable<Defect["history"]>[number]): string => {
    switch (entry.type) {
      case "triaged":
        return t("defectsPanel.history.triaged", { severity: entry.severity ? SEVERITY_LABEL[entry.severity] : "—" });
      case "assigned":
        return t("defectsPanel.history.assigned", { assignee: assigneeName(entry.assignee) });
      case "deadlineChanged":
        return t("defectsPanel.history.deadlineChanged", { from: formatDateTime(entry.from), to: formatDateTime(entry.to) });
      case "measureAdded":
        return t("defectsPanel.history.measureAdded", { kind: entry.kind ? t(`defectsPanel.measureKind.${entry.kind}`) : "—" });
      case "comment":
        return t("defectsPanel.history.comment");
      case "claimCheck":
        // Die Gewährleistungsweiche beim Melden: Vertrag gefunden, Anspruch erfasst — oder schon verjährt.
        return (entry as any).result === "noClaim"
          ? t("defectsPanel.history.claimCheckNoClaim")
          : t("defectsPanel.history.claimCheck", { date: formatLegalDate((entry as any).limitationEnd) });
      case "deadlineEventAdded":
      case "deadlineEventProposed":
      case "deadlineEventConfirmed":
      case "created":
      case "resolved":
      case "reopened":
      case "closed":
      case "discarded":
        return t(`defectsPanel.history.${entry.type}`);
      default:
        return entry.type;
    }
  };

  const info = defect?.assetInfo;

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {defect?.mark ? `${defect.mark} · ` : ""}
            {defect ? defectTitle(defect) : ""}
          </DialogTitle>
          <DialogDescription>
            {t("defectsPanel.detail.subtitle", {
              at: formatDateTime(defect?.registered),
              by: actorName(defect?.registeredBy),
            })}
          </DialogDescription>
        </DialogHeader>

        {defect ? (
          <div className="flex flex-col gap-5">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{t("defectsPanel.columns.status")}</dt>
              <dd className="flex items-center gap-2">
                <DefectStatusBadge status={defect.status} />
                {defect.overdue ? <Badge variant="destructive">{t("defectsPanel.overdue")}</Badge> : null}
              </dd>
              <dt className="text-muted-foreground">{t("defectsPanel.columns.severity")}</dt>
              <dd>
                {SEVERITY_LABEL[defect.severity]} — {t(`defectsPanel.severity.${defect.severity}`)}
              </dd>
              <dt className="text-muted-foreground">{t("defectsPanel.columns.asset")}</dt>
              <dd className="flex items-center gap-2">
                {info?.name || info?.id || defect.asset}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  aria-label={t("defectsPanel.detail.openAsset")}
                  onClick={() => navigate(`/entity?selectedEntityType=Asset&selectedEntity=${defect.asset}`)}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </dd>
              <dt className="text-muted-foreground">{t("defectsPanel.columns.deadline")}</dt>
              <dd>
                {formatDateTime(defect.deadline)}
                {defect.deadlineOverrideReason ? (
                  <span className="text-muted-foreground"> — {defect.deadlineOverrideReason}</span>
                ) : null}
              </dd>
              <dt className="text-muted-foreground">{t("defectsPanel.columns.assignee")}</dt>
              <dd>{assigneeName(defect.assignee)}</dd>
              {defect.source ? (
                <>
                  <dt className="text-muted-foreground">{t("defectsPanel.detail.source")}</dt>
                  <dd>{t(`defectsPanel.source.${defect.source}`)}</dd>
                </>
              ) : null}
              {defect.operation ? (
                <>
                  <dt className="text-muted-foreground">{t("defectsPanel.detail.operation")}</dt>
                  <dd>{t(`defectsPanel.operation.${defect.operation}`)}</dd>
                </>
              ) : null}
              {defect.openReason ? (
                <>
                  <dt className="text-muted-foreground">{t("defectsPanel.detail.openReason")}</dt>
                  <dd>
                    {t(`defectsPanel.openReason.${defect.openReason}`)}
                    {defect.openReasonNote ? ` — ${defect.openReasonNote}` : ""}
                  </dd>
                </>
              ) : null}
            </dl>

            {defect.description ? <p className="whitespace-pre-wrap text-sm">{defect.description}</p> : null}

            {defect.contract ? <WarrantySection customer={customer} defectId={defect._id} revision={defect.status} /> : null}

            {(defect.photos ?? []).length ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-medium">{t("defectsPanel.detail.photos")}</h3>
                <div className="flex flex-wrap gap-2">
                  {(defect.photos ?? []).map((photo, index) => {
                    const url = photoUrl(photo.path, customer, userAPI.getResourceURL);

                    return (
                      <a key={`${photo.path}-${index}`} href={url} target="_blank" rel="noreferrer" className="flex flex-col gap-1">
                        <img src={url} alt={t(`defectsPanel.photoKind.${photo.kind ?? "other"}`)} className="h-28 w-28 rounded-md border object-cover" />
                        <span className="text-xs text-muted-foreground">{t(`defectsPanel.photoKind.${photo.kind ?? "other"}`)}</span>
                      </a>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">{t("defectsPanel.detail.measures")}</h3>
              {(defect.measures ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("defectsPanel.detail.noMeasures")}</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {(defect.measures ?? []).map((measure, index) => (
                    <li key={index} className="rounded-md border border-border/60 p-2 text-sm">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-medium">{t(`defectsPanel.measureKind.${measure.kind}`)}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(measure.performedAt)} · {actorName(measure.performedBy)}
                        </span>
                      </div>
                      {measure.description ? <div className="whitespace-pre-wrap">{measure.description}</div> : null}
                      {(measure.materials ?? []).length ? (
                        <table className="mt-1 w-full text-xs">
                          <thead className="text-muted-foreground">
                            <tr>
                              <th className="text-left font-normal">{t("defectsPanel.material.name")}</th>
                              <th className="text-right font-normal">{t("defectsPanel.material.quantity")}</th>
                              <th className="text-left font-normal">{t("defectsPanel.material.unit")}</th>
                              <th className="text-left font-normal">{t("defectsPanel.material.billable")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(measure.materials ?? []).map((material, i) => (
                              <tr key={i}>
                                <td>{material.name}</td>
                                <td className="text-right">{material.quantity}</td>
                                <td>{material.unit}</td>
                                <td>{material.billable === false ? t("defectsPanel.no") : t("defectsPanel.yes")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">{t("defectsPanel.detail.history")}</h3>
              {(defect.history ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("defectsPanel.detail.noHistory")}</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {[...(defect.history ?? [])].reverse().map((entry, index) => (
                    <li key={`${entry.at ?? index}-${index}`} className="rounded-md border border-border/60 p-2 text-sm">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-medium">{describeEvent(entry)}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(entry.at)} · {actorName(entry.by)}
                        </span>
                      </div>
                      {entry.comment ? <div className="whitespace-pre-wrap text-xs">{entry.comment}</div> : null}
                      {entry.reason ? <div className="text-xs">{entry.reason}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <section className="flex flex-col gap-3 border-t pt-4">
              <div className="flex flex-wrap gap-2">
                {actions.map((candidate) => (
                  <Button
                    key={candidate}
                    size="sm"
                    variant={action === candidate ? "default" : "outline"}
                    onClick={() => startAction(candidate)}
                  >
                    {t(`defectsPanel.actions.${candidate}`)}
                  </Button>
                ))}
              </div>
              {action ? (
                <div className="flex flex-col gap-3 rounded-md border p-3">
                  {actionForm()}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setAction(undefined)}>
                      {t("defectsPanel.cancel")}
                    </Button>
                    <Button size="sm" disabled={submitting || !canSubmit(action, draft, defect)} onClick={() => void submit()}>
                      {t(`defectsPanel.actions.${action}`)}
                    </Button>
                  </div>
                </div>
              ) : null}
            </section>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
